<template lang="pug">
el-dialog.export-dialog(
    :title="$t('Export bookings')"
    :visible="visible"
    width="520px"
    top="12vh"
    append-to-body
    @close="$emit('close')"
)
    //- Nur in der Anmeldungs-Liste: die Veranstaltung waehlen
    .export-section(v-if="events.length")
        .export-section-head
            span.export-section-title {{ $t('Event') }}
        el-select.is-full-width(
            v-model="selectedEventId"
            size="small"
            filterable
            :placeholder="$t('Select event')"
        )
            el-option(
                v-for="event in events"
                :key="event.id"
                :label="event.name"
                :value="event.id"
            )

    .export-summary(v-if="summary")
        i.el-icon-fa-users.fas
        span {{ summary }}

    .export-section
        .export-section-head
            span.export-section-title {{ $t('Columns') }}
            el-checkbox(
                :indeterminate="someColumnsSelected"
                :value="allColumnsSelected"
                @change="toggleAllColumns"
            ) {{ $t('All') }}
        el-checkbox-group(v-model="selectedColumns")
            .export-groups
                .export-group(v-for="group in columnGroups" :key="group.key")
                    p.export-group-label {{ group.label }}
                    el-checkbox(
                        v-for="column in group.columns"
                        :key="column.key"
                        :label="column.key"
                    ) {{ column.label }}

    .export-section
        .export-section-head
            span.export-section-title {{ $t('Sorting') }}
        el-select.is-full-width(v-model="sortBy" size="small")
            el-option(:label="$t('By name')" value="name")
            el-option(:label="$t('By registration date')" value="registered")

    .export-section
        .export-section-head
            span.export-section-title {{ $t('PDF options') }}
        el-switch.export-option(
            v-if="qrCodeAvailable"
            v-model="showQrCode"
            :active-text="$t('Show QR code')"
        )
        p.help(v-if="qrCodeAvailable") {{ $t('Opens the scanner app for this event.') }}
        .export-login(v-if="qrCodeAvailable && showQrCode && loginOptions.length")
            span.export-login-label {{ $t('App-Benutzer Vorauswahl') }}
            el-select(
                :value="loginEmail"
                size="mini"
                clearable
                filterable
                :placeholder="$t('Keine Vorauswahl')"
                @input="email => $emit('update:loginEmail', email || '')"
            )
                el-option(
                    v-for="option in loginOptions"
                    :key="option.email"
                    :label="option.label"
                    :value="option.email"
                )
        el-switch.export-option(
            v-model="landscape"
            :active-text="$t('Landscape format')"
        )
        p.help {{ $t('Recommended for many columns. The CSV export is not affected.') }}

    template(slot="footer")
        el-button(
            size="mini"
            icon="el-icon-fa-times fas"
            @click="$emit('close')"
        ) {{ $t('Dismiss') }}
        el-button(
            size="mini"
            icon="el-icon-fa-file-csv fas"
            :loading="csvLoading"
            :disabled="!canExport"
            @click="exportCsv"
        ) {{ $t('CSV') }}
        el-button(
            type="primary"
            size="mini"
            icon="el-icon-fa-file-pdf fas"
            :disabled="!canExport"
            @click="exportPdf"
        ) {{ $t('PDF') }}
</template>

<i18n>
{
    "de": {
        "Export bookings": "Anmeldungen exportieren",
        "Event": "Veranstaltung",
        "Select event": "Veranstaltung wählen",
        "{count} bookings": "{count} Anmeldungen",
        "{count} checked in": "{count} eingecheckt",
        "Columns": "Spalten",
        "Registration form": "Anmeldeformular",
        "All": "Alle",
        "Sorting": "Sortierung",
        "By name": "Nach Namen",
        "By registration date": "Nach Anmeldedatum",
        "Contact data": "Kontaktdaten",
        "Registration": "Anmeldung",
        "Person": "Person",
        "Email": "E-Mail",
        "Phone": "Telefon",
        "Company": "Firma",
        "Slot": "Slot",
        "Registered at": "Angemeldet am",
        "Message": "Nachricht",
        "Scanned": "Gescannt",
        "PDF options": "PDF-Optionen",
        "Show QR code": "QR-Code anzeigen",
        "Opens the scanner app for this event.": "Öffnet die Scanner-App für dieses Event.",
        "App-Benutzer Vorauswahl": "App-Benutzer Vorauswahl",
        "Keine Vorauswahl": "Keine Vorauswahl",
        "Landscape format": "Querformat",
        "Recommended for many columns. The CSV export is not affected.": "Empfohlen bei vielen Spalten. Der CSV-Export bleibt davon unberührt.",
        "Dismiss": "Abbrechen",
        "An error occurred!": "Ein Fehler ist aufgetreten!",
        "Bookings could not be fetched.": "Anmeldungen konnten nicht abgerufen werden."
    },
    "en": {
        "{count} bookings": "{count} bookings",
        "{count} checked in": "{count} checked in"
    }
}
</i18n>

<script>
import _ from 'lodash'

import { timestamp2date } from '@/filters'
import EventApi from '@/extensions/eventManager/store/api/event'
import RegistrationTemplateApi from '@/extensions/eventManager/store/api/registrationTemplate'
import { answersByField } from '@/extensions/eventManager/views/Partials/RegistrationFieldInputs'

const DEFAULT_COLUMNS = ['name', 'email', 'phone', 'slot', 'registered', 'scanned']

export default {
    name: 'BookingsExportDialog',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        // Nicht leer: der Dialog zeigt eine Veranstaltungs-Auswahl
        events: {
            type: Array,
            default: () => []
        },
        eventId: {
            type: [Number, String],
            default: null
        },
        // Name der Veranstaltung fuer den Dateinamen des CSV-Exports
        eventName: {
            type: String,
            default: ''
        },
        // Bereits geladene Anmeldungen (Event-Formular) — sonst werden sie
        // fuer den CSV-Export der gewaehlten Veranstaltung nachgeladen
        bookings: {
            type: Array,
            default: null
        },
        // "QR-Code verwenden" der Veranstaltung; in der Anmeldungs-Liste kommt
        // der Wert aus der gewaehlten Veranstaltung
        qrEnabled: {
            type: Boolean,
            default: true
        },
        loginOptions: {
            type: Array,
            default: () => []
        },
        loginEmail: {
            type: String,
            default: ''
        },
        // Felder des Anmeldeformulars (Event-Formular); in der Anmeldungs-Liste werden sie
        // fuer die gewaehlte Veranstaltung nachgeladen
        templateFields: {
            type: Array,
            default: null
        }
    },
    data() {
        return {
            selectedEventId: this.eventId,
            selectedColumns: [...DEFAULT_COLUMNS],
            sortBy: 'name',
            landscape: false,
            showQrCode: true,
            csvLoading: false,
            // nachgeladene Felder des Anmeldeformulars der gewaehlten Veranstaltung
            loadedTemplate: { id: null, fields: [] },
        }
    },
    computed: {
        selectedEvent() {
            return this.events.find(event => String(event.id) === String(this.selectedEventId))
        },
        // Ohne QR-Codes gibt es kein Einchecken: keine Eingecheckt-Spalte und
        // keine QR-Optionen im PDF
        qrCodeAvailable() {
            if (this.events.length) return !!_.get(this.selectedEvent, 'use_qr_code')
            return this.qrEnabled
        },
        // Spalten des Exports; die Reihenfolge im PDF bestimmt die API
        columns() {
            const columns = [
                { key: 'name', label: this.$t('Person'), group: 'contact', get: booking => this.bookerName(booking) },
                { key: 'email', label: this.$t('Email'), group: 'contact', get: booking => _.get(booking, 'booker.email', '') },
                { key: 'phone', label: this.$t('Phone'), group: 'contact', get: booking => _.get(booking, 'booker.phone', '') },
                { key: 'company', label: this.$t('Company'), group: 'contact', get: booking => _.get(booking, 'booker.company', '') },
                { key: 'slot', label: this.$t('Slot'), group: 'registration', get: booking => this.slotLabel(booking) },
                { key: 'registered', label: this.$t('Registered at'), group: 'registration', get: booking => booking.created_at ? timestamp2date(booking.created_at, 'dd.MM.yyyy HH:mm') : '' },
                { key: 'message', label: this.$t('Message'), group: 'registration', get: booking => booking.message || '' },
            ]

            // Zusatzfragen des Anmeldeformulars, in Formular-Reihenfolge
            this.customTemplateFields.forEach(field => {
                columns.push({
                    key: `field:${field.id}`,
                    label: field.label,
                    group: 'form',
                    get: booking => answersByField(booking.values)[field.id] || '',
                })
            })

            // Eingecheckt bleibt immer die letzte Spalte
            if (this.qrCodeAvailable) {
                columns.push({ key: 'scanned', label: this.$t('Scanned'), group: 'registration', get: booking => booking.scanned ? '✓' : '' })
            }

            return columns
        },
        columnGroups() {
            const groups = [
                { key: 'contact', label: this.$t('Contact data') },
                { key: 'registration', label: this.$t('Registration') },
                { key: 'form', label: this.$t('Registration form') },
            ]

            return groups
                .map(group => ({
                    ...group,
                    columns: this.columns.filter(column => column.group === group.key),
                }))
                // ohne Anmeldeformular gibt es keine Formular-Gruppe
                .filter(group => group.columns.length)
        },
        // Felder des Anmeldeformulars: mitgegeben (Event-Formular) oder nachgeladen (Liste)
        customTemplateFields() {
            const fields = this.templateFields !== null ? this.templateFields : this.loadedTemplate.fields
            return (fields || []).filter(field => !field.key)
        },
        selectedTemplateId() {
            if (this.templateFields !== null) return null
            return _.get(this.selectedEvent, 'registrationTemplate.id') || null
        },
        allColumnsSelected() {
            return this.selectedColumns.length === this.columns.length
        },
        someColumnsSelected() {
            return !!this.selectedColumns.length && !this.allColumnsSelected
        },
        canExport() {
            return !!this.selectedColumns.length && !!this.selectedEventId
        },
        // Umfang des Exports — nur wenn die Anmeldungen vorliegen
        summary() {
            if (!this.bookings) return ''

            const list = this.bookings.filter(booking => !booking.deleted)
            const scanned = list.filter(booking => booking.scanned).length

            let summary = this.$t('{count} bookings', { count: list.length })
            if (scanned) summary += ` · ${this.$t('{count} checked in', { count: scanned })}`

            return summary
        },
        selectedEventName() {
            const event = this.events.find(item => String(item.id) === String(this.selectedEventId))
            return _.get(event, 'name') || this.eventName
        }
    },
    watch: {
        eventId(id) {
            this.selectedEventId = id
        },
        // Anmeldungs-Liste: das Formular der gewaehlten Veranstaltung nachladen
        selectedTemplateId: {
            immediate: true,
            handler(id) {
                if (!id) {
                    this.loadedTemplate = { id: null, fields: [] }
                    return
                }
                if (this.loadedTemplate.id === id) return
                this.loadedTemplate = { id, fields: [] }
                RegistrationTemplateApi.fetchRegistrationTemplate(id)
                    .then(template => {
                        if (this.loadedTemplate.id !== id) return
                        this.loadedTemplate.fields = _.get(template, 'fields') || []
                    })
                    .catch(err => {
                        /* eslint no-console: ["error", { allow: ["error"] }] */
                        console.error(err)
                    })
            }
        },
        // Wechselt das Formular, verschwinden Spalten, die es nicht mehr gibt
        columns(columns) {
            const keys = columns.map(column => column.key)
            this.selectedColumns = this.selectedColumns.filter(key => keys.includes(key))
        },
        // Veranstaltung ohne QR-Codes: die Eingecheckt-Spalte faellt weg
        qrCodeAvailable(available) {
            if (!available) {
                this.selectedColumns = this.selectedColumns.filter(key => key !== 'scanned')
            }
        }
    },
    methods: {
        bookerName(booking) {
            const booker = booking.booker || {}
            return booker.name
                || [booker.first_name, booker.last_name].filter(name => name && name !== '-').join(' ')
                || booker.email
                || ''
        },
        slotLabel(booking) {
            const slot = booking.slot
            if (!slot) return ''

            return `${timestamp2date(slot.start_at)} ${timestamp2date(slot.start_at, 'HH:mm')}–${timestamp2date(slot.end_at, 'HH:mm')}`
        },
        toggleAllColumns(checked) {
            this.selectedColumns = checked ? this.columns.map(column => column.key) : []
        },
        activeColumns() {
            return this.columns.filter(column => this.selectedColumns.includes(column.key))
        },
        exportPdf() {
            this.$emit('export-pdf', {
                eventId: this.selectedEventId,
                columns: this.selectedColumns,
                sort: this.sortBy,
                landscape: this.landscape,
                showQrCode: this.qrCodeAvailable && this.showQrCode,
            })
            this.$emit('close')
        },
        // Gleiche Reihenfolge wie im PDF (siehe EventBookings::fetchBookings)
        sortBookings(bookings) {
            if (this.sortBy === 'registered') {
                return _.orderBy(bookings, [booking => booking.created_at || 0], ['asc'])
            }

            return _.orderBy(bookings, [booking => this.bookerName(booking).toLowerCase()], ['asc'])
        },
        // Anmeldungen der gewaehlten Veranstaltung: entweder vom Formular
        // mitgegeben oder ueber die Veranstaltung nachgeladen
        resolveBookings() {
            if (this.bookings) {
                return Promise.resolve(this.bookings.filter(booking => !booking.deleted))
            }

            return EventApi.fetchEvent(this.selectedEventId).then(event => {
                let bookings = []

                _.each(_.get(event, 'slots') || [], slot => {
                    const slotBookings = _.cloneDeep(slot.bookings || [])
                    _.each(slotBookings, booking => booking.slot = _.omit(slot, ['bookings']))
                    bookings = [...bookings, ...slotBookings]
                })

                return bookings
            })
        },
        // Excel-kompatibel: Semikolon als Trenner + BOM
        exportCsv() {
            this.csvLoading = true

            this.resolveBookings()
                .then(bookings => {
                    const list = this.sortBookings(bookings)
                    const active = this.activeColumns()
                    const separator = ';'
                    const escape = value => {
                        const text = String(value == null ? '' : value)
                        return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
                    }

                    const header = ['#', ...active.map(column => column.label)].map(escape).join(separator)
                    const lines = list.map((booking, index) =>
                        [index + 1, ...active.map(column => column.get(booking))].map(escape).join(separator)
                    )
                    const csv = '﻿' + [header, ...lines].join('\r\n')

                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                    const url = URL.createObjectURL(blob)
                    const link = document.createElement('a')
                    link.href = url
                    link.download = `${_.kebabCase(this.selectedEventName) || 'event'}-anmeldungen.csv`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(url)

                    this.$emit('close')
                })
                .catch(() => this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('Bookings could not be fetched.'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                }))
                .finally(() => this.csvLoading = false)
        }
    }
}
</script>

<style lang="sass" scoped>
.export-summary
    display: flex
    align-items: center
    gap: 8px
    padding: 9px 12px
    margin-bottom: 20px
    background: $--background-color-base
    border-radius: 3px
    font-size: 13px
    color: $--color-text-regular
    i
        color: $--color-primary

.export-section
    & + .export-section
        margin-top: 22px
        padding-top: 18px
        border-top: 1px solid $--border-color-lighter

    .export-section-head
        display: flex
        align-items: center
        justify-content: space-between
        margin-bottom: 10px

    .export-section-title
        font-size: 12px
        font-weight: 600
        letter-spacing: .04em
        text-transform: uppercase
        color: $--color-text-secondary

    .export-groups
        display: grid
        grid-template-columns: 1fr 1fr
        gap: 20px

    .export-group-label
        margin: 0 0 6px
        font-size: 12px
        color: $--color-text-secondary

    .export-group .el-checkbox
        display: block
        margin: 0 0 6px
        &:last-child
            margin-bottom: 0

    .export-option + .help + .export-option
        margin-top: 14px

    .export-login
        display: flex
        align-items: center
        gap: 10px
        margin-top: 10px

        .export-login-label
            font-size: 12px
            color: $--color-text-secondary

        .el-select
            flex-grow: 1

    .export-login + .export-option
        margin-top: 14px

    .help
        margin: 8px 0 0
        line-height: 1.4
</style>
